'use client';

import Image from 'next/image';
import Link from 'next/link';

import { useQuery } from '@tanstack/react-query';
import { ArrowRight, CalendarDays } from 'lucide-react';

import { resolveTimezone, tenantCalendarDayKey } from '@kit/shared/timezone';
import { formatLocaleDate, formatTimeToAmPm } from '@kit/shared/utils';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { cn } from '@kit/ui/utils';

import {
  type LandingScheduleSession,
  landingScheduleQueryOptions,
} from '~/(tenant)/_lib/landing-schedule';
import type { ScheduleBlock } from '~/(tenant)/_lib/schema/landing-config.schema';

import {
  type LandingRenderContext,
  headingFont,
  landingImageUrl,
  mutedText,
  pickLocalized,
} from './landing-context';
import styles from './schedule-block.module.css';

export function LandingScheduleBlock({
  block,
  ctx,
}: {
  block: ScheduleBlock;
  ctx: LandingRenderContext;
}) {
  const client = useSupabase();
  const { data, isLoading, isError, dataUpdatedAt } = useQuery(
    landingScheduleQueryOptions(client, ctx.accountSlug, block.daysAhead, ctx.timeZone),
  );
  const sessions = data?.slice(0, block.limit ?? 12) ?? [];
  const isAr = ctx.locale.split('-')[0] === 'ar';
  const title = pickLocalized(block.title, block.title_ar, ctx.locale);
  const eyebrow = pickLocalized(block.eyebrow, block.eyebrow_ar, ctx.locale);
  const subtitle = pickLocalized(block.subtitle, block.subtitle_ar, ctx.locale);

  if (sessions.length === 0 && (block.hideWhenEmpty || (!isLoading && !title))) return null;

  if (block.layout === 'poster') {
    return (
      <PosterSchedule
        block={block}
        ctx={ctx}
        sessions={sessions}
        isLoading={isLoading}
        isError={isError}
        updatedAt={dataUpdatedAt}
      />
    );
  }

  return (
    <section data-test="landing-schedule" dir={isAr ? 'rtl' : 'ltr'} className="py-16 lg:py-24">
      <div className="landing-content mx-auto max-w-7xl px-6 lg:px-8">
        {(title || eyebrow || subtitle) && (
          <div className="mb-8 flex flex-wrap items-end justify-between gap-6">
            <div className="landing-section-header max-w-2xl">
              {eyebrow && (
                <p className="text-primary mb-3 text-xs font-semibold tracking-[0.2em] uppercase">
                  {eyebrow}
                </p>
              )}
              {title && (
                <h2 className={cn('text-3xl font-semibold sm:text-4xl', headingFont(ctx.serif))}>
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className={cn('mt-3 text-base leading-relaxed', mutedText(ctx.dark))}>
                  {subtitle}
                </p>
              )}
            </div>
            <Link
              href="/book"
              className="landing-button inline-flex min-h-11 items-center rounded-full border border-current px-5 py-2 text-sm font-medium transition-opacity hover:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-4"
            >
              {isAr ? 'عرض الجدول الكامل' : 'View full schedule'}
            </Link>
          </div>
        )}

        {isLoading ? (
          <div role="status" className="flex gap-4 overflow-hidden" aria-label={isAr ? 'جارٍ تحميل الحصص' : 'Loading classes'}>
            {[0, 1, 2].map((index) => (
              <div key={index} className="h-80 w-64 shrink-0 animate-pulse rounded-xl bg-current opacity-10" />
            ))}
          </div>
        ) : sessions.length > 0 ? (
          <ul className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4">
            {sessions.map((session) => (
              <li key={session.booking_id} className="w-[min(80vw,280px)] shrink-0 snap-start">
                <ScheduleCard session={session} ctx={ctx} />
              </li>
            ))}
          </ul>
        ) : (
          <p role="status" className={cn('py-8 text-sm', mutedText(ctx.dark))}>
            {isError
              ? isAr
                ? 'تعذر تحميل الحصص الآن. يرجى زيارة صفحة الحجز لعرض الجدول.'
                : 'Classes could not be loaded. Visit the booking page to see the schedule.'
              : isAr
                ? 'لا توجد حصص قادمة في هذه الفترة. استكشف الجدول الكامل.'
                : 'No upcoming classes in this period. Explore the full schedule.'}
          </p>
        )}
      </div>
    </section>
  );
}

function PosterSchedule({
  block,
  ctx,
  sessions,
  isLoading,
  isError,
  updatedAt,
}: {
  block: ScheduleBlock;
  ctx: LandingRenderContext;
  sessions: LandingScheduleSession[];
  isLoading: boolean;
  isError: boolean;
  updatedAt: number;
}) {
  const isAr = ctx.locale.split('-')[0] === 'ar';
  const title = pickLocalized(block.title, block.title_ar, ctx.locale);
  const eyebrow = pickLocalized(block.eyebrow, block.eyebrow_ar, ctx.locale);
  const subtitle = pickLocalized(block.subtitle, block.subtitle_ar, ctx.locale);
  const today = tenantCalendarDayKey(updatedAt, resolveTimezone(ctx.timeZone));
  const tomorrow = new Date(`${today}T00:00:00Z`);
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  const tomorrowKey = Number.isNaN(tomorrow.getTime()) ? '' : tomorrow.toISOString().slice(0, 10);

  return (
    <section data-test="landing-schedule" data-layout="poster" dir={isAr ? 'rtl' : 'ltr'} className="py-16 lg:py-24">
      <div className="landing-content mx-auto max-w-[1320px] px-6 lg:px-8">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div className="landing-section-header">
            {eyebrow && (
              <p className={cn(styles.label, 'mb-3 text-xs')} style={{ color: block.accentColors?.[1] ?? 'var(--primary)' }}>
                {eyebrow}
              </p>
            )}
            {title && <h2 className={cn('text-3xl sm:text-4xl', headingFont(ctx.serif))}>{title}</h2>}
            {subtitle && <p className={cn('mt-1 text-sm', mutedText(ctx.dark))}>{subtitle}</p>}
          </div>
          <Link href="/book" className={cn(styles.fullSchedule, 'landing-button focus-visible:outline-2 focus-visible:outline-offset-4')}>
            {pickLocalized(block.ctaText, block.ctaText_ar, ctx.locale) || (isAr ? 'عرض الجدول الكامل' : 'View Full Schedule')}
          </Link>
        </div>
        {isLoading ? (
          <div role="status" aria-label={isAr ? 'جارٍ تحميل الحصص' : 'Loading classes'} className="flex gap-4 overflow-hidden">
            {[0, 1, 2].map((index) => <div key={index} className="h-[280px] w-[220px] shrink-0 animate-pulse bg-current opacity-10" />)}
          </div>
        ) : sessions.length > 0 ? (
          <ul className={cn(styles.scroller, 'flex gap-4 overflow-x-auto pb-4')} aria-label={title || eyebrow || (isAr ? 'الحصص القادمة' : 'Upcoming classes')} tabIndex={0}>
            {sessions.map((session, index) => (
              <li key={session.booking_id} className="w-[220px] shrink-0 snap-start">
                <PosterScheduleCard session={session} block={block} ctx={ctx} index={index} today={today} tomorrow={tomorrowKey} />
              </li>
            ))}
          </ul>
        ) : (
          <p role="status" className={cn('py-8 text-sm', mutedText(ctx.dark))}>
            {isError
              ? isAr ? 'تعذر تحميل الحصص الآن. يرجى زيارة صفحة الحجز لعرض الجدول.' : 'Classes could not be loaded. Visit the booking page to see the schedule.'
              : isAr ? 'لا توجد حصص قادمة في هذه الفترة. استكشف الجدول الكامل.' : 'No upcoming classes in this period. Explore the full schedule.'}
          </p>
        )}
      </div>
    </section>
  );
}

function PosterScheduleCard({ session, block, ctx, index, today, tomorrow }: {
  session: LandingScheduleSession;
  block: ScheduleBlock;
  ctx: LandingRenderContext;
  index: number;
  today: string;
  tomorrow: string;
}) {
  const locale = ctx.locale.split('-')[0] === 'ar' ? 'ar' : 'en';
  const isAr = locale === 'ar';
  const name = pickLocalized(session.class_name, session.class_name_alt, locale);
  const description = pickLocalized(session.class_description, session.class_description_alt, locale);
  const dateLabel = session.booking_date === today
    ? isAr ? 'اليوم' : 'Today'
    : session.booking_date === tomorrow
      ? isAr ? 'غداً' : 'Tomorrow'
      : new Intl.DateTimeFormat(isAr ? 'ar-SA' : 'en-US', { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${session.booking_date}T00:00:00Z`));
  const minutes = (time: string) => {
    const [hours = 0, mins = 0] = time.split(':').map(Number);
    return hours * 60 + mins;
  };
  const duration = session.end_time ? minutes(session.end_time) - minutes(session.start_time) : 0;
  const classNames = `${session.class_name} ${session.class_name_alt ?? ''}`.toLowerCase();
  const matchedImage = block.imageRules?.find((rule) => rule.keywords?.some((keyword) => classNames.includes(keyword.toLowerCase())))?.imageKey;
  const fallbackImage = matchedImage ?? block.fallbackImageKeys?.[index % block.fallbackImageKeys.length];
  const image = session.image_url || (fallbackImage ? landingImageUrl(ctx, fallbackImage) : '');
  const accent = block.accentColors?.[index % block.accentColors.length] ?? 'var(--primary)';

  return (
    <Link href={`/event/${session.booking_id}`} data-test="landing-schedule-session" className="group block focus-visible:outline-2 focus-visible:outline-offset-4">
      <div className="relative h-[280px] overflow-hidden" style={{ background: 'var(--primary)' }}>
        {image ? (
          <Image src={image} alt={name ?? ''} fill sizes="220px" unoptimized={Boolean(session.image_url)} className="object-cover transition-transform duration-500 motion-safe:group-hover:scale-105" />
        ) : (
          <div className="flex h-full items-center justify-center"><CalendarDays className="size-12 opacity-40" aria-hidden="true" strokeWidth={1} /></div>
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.35) 62%, rgba(0,0,0,0.78) 100%)' }} />
        <div className="absolute right-4 bottom-4 left-4 text-white">
          <p className={cn(styles.label, 'mb-1 text-[0.6rem] text-white/90')}>
            {dateLabel} · {formatTimeToAmPm(session.start_time, locale)}
            {duration > 0 ? ` · ${duration} ${isAr ? 'دقيقة' : 'min'}` : ''}
          </p>
          <h3 className={cn('text-xl leading-tight', headingFont(ctx.serif))}>{name}</h3>
          {session.trainer_name && <p className="mt-1 text-[0.7rem] text-white/90">{isAr ? 'مع' : 'with'} {session.trainer_name}</p>}
        </div>
      </div>
      <div className="mt-3">
        {description && <p className={cn('mb-2.5 line-clamp-2 text-xs leading-relaxed', mutedText(ctx.dark))}>{description}</p>}
        <span className={cn(styles.label, 'inline-flex items-center gap-1 text-[0.6rem] transition-all group-hover:gap-1.5')} style={{ color: accent }}>
          {pickLocalized(block.cardCtaText, block.cardCtaText_ar, ctx.locale) || (isAr ? 'احجزوا هذا الكلاس' : 'Book This Class')}
          <ArrowRight size={11} className="rtl:-scale-x-100" aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
}

function ScheduleCard({
  session,
  ctx,
}: {
  session: LandingScheduleSession;
  ctx: LandingRenderContext;
}) {
  const locale = ctx.locale.split('-')[0] === 'ar' ? 'ar' : 'en';
  const name = pickLocalized(session.class_name, session.class_name_alt, locale);
  const description = pickLocalized(session.class_description, session.class_description_alt, locale);

  return (
    <Link
      href={`/event/${session.booking_id}`}
      data-test="landing-schedule-session"
      className="landing-card group block h-full overflow-hidden rounded-xl border border-current/15 focus-visible:outline-2 focus-visible:outline-offset-4"
    >
      <div className="landing-image relative aspect-[4/3] overflow-hidden">
        {session.image_url ? (
          <Image
            data-motion-image
            src={session.image_url}
            alt={name ?? ''}
            fill
            sizes="280px"
            unoptimized
            className="object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-current/5">
            <CalendarDays className="size-12 opacity-40" aria-hidden="true" strokeWidth={1} />
          </div>
        )}
      </div>
      <div className="space-y-3 p-5">
        <p className={cn('text-xs', mutedText(ctx.dark))}>
          {formatLocaleDate(session.booking_date, locale, { weekday: 'short', month: 'short', day: 'numeric', year: undefined })}
          {' · '}
          {formatTimeToAmPm(session.start_time, locale)}
        </p>
        <h3 className={cn('text-xl font-semibold', headingFont(ctx.serif))}>{name}</h3>
        {session.trainer_name && (
          <p className="text-sm">{session.trainer_name}</p>
        )}
        {description && (
          <p className={cn('line-clamp-2 text-sm leading-relaxed', mutedText(ctx.dark))}>{description}</p>
        )}
        <p className="text-sm font-medium underline underline-offset-4">
          {locale === 'ar' ? 'عرض الحصة' : 'View class'}
        </p>
      </div>
    </Link>
  );
}
